import PropTypes from 'prop-types';
import {Tooltip} from 'antd';
import styles from './financial.module.scss';
import {MetricsEmpty} from './MetricsEmpty';

/**
 * One currency's figure inside a metric section.
 *
 * The amount used to be painted per currency — purple for USD, green for EUR,
 * navy for the CFA francs — so colour varied with something that carries no
 * meaning while reading as if it did. Colour now belongs to status alone; the
 * currency is a label, and the number is the thing that stands out.
 */

const STATUS_TONE = {
  FILLED: 'toneOk',
  SETTLED: 'toneOk',
  COMPLETED: 'toneOk',
  FUNDED: 'toneOk',
  PENDING: 'toneWarn',
  PARTIALLY_FILLED: 'toneWarn',
  OPEN: 'toneInfo',
  CREATED: 'toneInfo',
  ERROR: 'toneStop',
  FAILED: 'toneStop',
  REJECTED: 'toneStop',
  CANCELLED: 'toneStop',
};

export const FinancialCurrencyCard = ({
  currency,
  loading,
  symbol,
  amount,
  feeType,
  status,
  infoTooltip,
}) => {
  if (loading) return <div className={styles.loadingSkeleton} />;

  const tone = status ? STATUS_TONE[String(status).toUpperCase()] : null;

  return (
    <div className={styles.currencyCard}>
      <div className={styles.cardHeader}>
        <p className={styles.currencyLabel}>
          <span>{currency}</span>
          {status && (
            <span
              className={`${styles.statusBadge} ${tone ? styles[tone] : ''}`}>
              {status}
            </span>
          )}
        </p>
        <div className={styles.cardHeaderRight}>
          {feeType && <span className={styles.feeTypeBadge}>{feeType}</span>}
          {infoTooltip ? (
            <Tooltip title={infoTooltip} placement="left">
              <span className={styles.infoIcon}>i</span>
            </Tooltip>
          ) : null}
        </div>
      </div>
      <h1 className={styles.amount}>
        {symbol ? (
          <span className={styles.currencySymbol}>{symbol}</span>
        ) : null}
        {amount?.toLocaleString()}
      </h1>
    </div>
  );
};

FinancialCurrencyCard.propTypes = {
  currency: PropTypes.string.isRequired,
  symbol: PropTypes.string,
  // Retained so existing call sites keep working; the card no longer colours
  // itself by currency.
  type: PropTypes.string,
  amount: PropTypes.number,
  loading: PropTypes.bool,
  feeType: PropTypes.string,
  status: PropTypes.string,
  infoTooltip: PropTypes.node,
};

FinancialCurrencyCard.defaultProps = {
  amount: 0,
  loading: false,
  feeType: null,
  infoTooltip: null,
};

/** Cards for one currency list, with the loading and empty states it needs. */
export const renderCurrencyList = (list, loading, options = {}) => {
  if (loading) {
    return Array(3)
      .fill('')
      .map((_, key) => <div key={key} className={styles.loadingSkeleton} />);
  }
  if (!list || !list.length) {
    return <MetricsEmpty>{options.empty}</MetricsEmpty>;
  }
  return list.map((item, key) => (
    <FinancialCurrencyCard
      key={key}
      type={item?.currency}
      currency={
        options.labelField ? item?.[options.labelField] : item?.currency
      }
      symbol={item?.symbol}
      amount={options.amountField ? item?.[options.amountField] : item?.amount}
      feeType={options.feeTypeField ? item?.[options.feeTypeField] : null}
    />
  ));
};
